import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import colors from 'hsl-shared-components/lib/Theme/colors';
import { Trans } from 'react-i18next';
import i18next from 'i18next';
import { getTicketPrice, formatPrice } from '../../../price';
import {
  TICKET_TYPE_ADDITIONAL_ZONE,
  MOBILE_BREAKPOINT_IN_PX,
} from '../../../constants';
import {
  getRelativeHeight,
  getRelativeWidth,
} from '../../../components/CardReader/CardReader';

const AdditionalZoneConfirmation = styled.div`
  position: absolute;
  top: ${getRelativeHeight(76)};
  left: ${getRelativeWidth(13)};
  right: ${getRelativeWidth(13)};
  bottom: ${getRelativeHeight(43)};
  color: ${colors.primary.hslGreyDark};
`;

const ValueTicketTitle = styled.div`
  position: absolute;
  top: -33.5%;
  left: 1.333333%;
  font-weight: 500;
  color: ${colors.primary.hslWhite};
`;

const AdditionalTickets = styled.div`
  position: absolute;
  font-size: 1.4em;
  top: ${getRelativeHeight(-22)};
  left: ${getRelativeWidth(34)};
`;

const AdditionalTicket = styled.div`
  display: flex;
  flex-direction: row;
  padding: ${getRelativeHeight(6)} 0 ${getRelativeHeight(13)};

  & > div {
    font-size: 1.4em;
    font-weight: 700;

    &:first-child {
      font-size: 0.95em;
      text-align: left;
      flex-grow: 1;
      align-self: center;
    }
  }
`;

const AdditionalTicketMultiplier = styled.span`
  font-weight: 300;
`;

const AdditionalTicketZoneText = styled.span`
  font-size: 1em;
`;

const AdditionalZoneValidity = styled.div`
  position: absolute;
  font-size: 0.95em;
  top: ${getRelativeHeight(54)};
  left: ${getRelativeWidth(6)};
`;

const AdditionalZoneValueValidity = styled.div`
  position: absolute;
  font-size: 0.75em;
  top: ${getRelativeHeight(196)};
  left: ${getRelativeWidth(5)};

  @media screen and (max-width: ${MOBILE_BREAKPOINT_IN_PX}px) {
    font-size: 7px;
  }
`;

const TotalPrice = styled.div`
  position: absolute;
  font-size: 0.85em;
  font-weight: 700;
  top: ${getRelativeHeight(194)};
  right: ${getRelativeWidth(210)};
`;

const TotalValuePrice = styled.div`
  position: absolute;
  font-size: 0.75em;
  font-weight: 700;
  top: ${getRelativeHeight(196)};
  right: ${getRelativeWidth(210)};

  @media screen and (max-width: ${MOBILE_BREAKPOINT_IN_PX}px) {
    font-size: 7px;
  }
`;

const RemainingFundsTitle = styled.div`
  color: ${colors.primary.hslGreyDark};
  position: absolute;
  font-size: 0.85em;
  font-weight: 300;
  top: ${getRelativeHeight(264)};
  left: ${getRelativeWidth(5)};
`;

const RemainingFunds = styled.div`
  position: absolute;
  font-size: 0.85em;
  font-weight: 700;
  top: ${getRelativeHeight(264)};
  right: ${getRelativeWidth(210)};
`;

const SeasonalTicketTitle = styled.div`
  position: absolute;
  top: -33.5%;
  right: 24.333333%;
  font-weight: 500;
  color: ${colors.primary.hslWhite};
`;

const SeasonalTicketZone = styled.div`
  position: absolute;
  font-size: 1.4em;
  font-weight: 700;
  top: ${getRelativeHeight(-16)};
  left: ${getRelativeWidth(242)};
`;

const SeasonalTicketValidity = styled.div`
  position: absolute;
  font-size: 0.85em;
  top: ${getRelativeHeight(54)};
  left: ${getRelativeWidth(214)};
`;

const AdditionalZoneConfirmationScreen = ({
  additionalZoneTicket,
  valueTicket,
  seasonalTicket,
  remainingFunds,
}) => {
  const valueTicketPrice = (valueTicket && (valueTicket.price * valueTicket.passengers)) || 0; // eslint-disable-line
  const { additionalZones } = additionalZoneTicket;
  const hasAdditionalZones = additionalZones && additionalZones.length > 0;
  const additionalZonesTotalPrice = getTicketPrice(
    TICKET_TYPE_ADDITIONAL_ZONE,
    additionalZones,
  );
  const totalPrice = formatPrice(valueTicketPrice + additionalZonesTotalPrice);
  return (
    <AdditionalZoneConfirmation>
      <ValueTicketTitle>
        <Trans i18nKey="Generic.singleTicket" />
      </ValueTicketTitle>
      <AdditionalTickets>
        {valueTicket && (
          <AdditionalTicket>
            <div>
              {valueTicket.passengers}{' '}
              <AdditionalTicketMultiplier>x</AdditionalTicketMultiplier>{' '}
              {valueTicket.zones}
            </div>
          </AdditionalTicket>
        )}
        {hasAdditionalZones && (
          <AdditionalTicket>
            <div>
              {i18next.language !== 'fi' && (
                <AdditionalTicketZoneText>
                  <Trans i18nKey="Generic.additionalZone" />{' '}
                </AdditionalTicketZoneText>
              )}
              {additionalZones}
              {i18next.language === 'fi' && (
                <AdditionalTicketZoneText>
                  <Trans i18nKey="Generic.additionalZone" />
                </AdditionalTicketZoneText>
              )}
            </div>
          </AdditionalTicket>
        )}
      </AdditionalTickets>
      {valueTicket && (
        <>
          <AdditionalZoneValueValidity>
            <Trans i18nKey="Generic.validityAbbr" />{' '}
            <strong>{additionalZoneTicket.validityEndsAtString}</strong>
          </AdditionalZoneValueValidity>
          <TotalValuePrice>
            <Trans i18nKey="Generic.charged" /> {totalPrice}
          </TotalValuePrice>
        </>
      )}
      {!valueTicket && (
        <>
          <AdditionalZoneValidity>
            <Trans i18nKey="Generic.validityAbbr" />{' '}
            <strong>{additionalZoneTicket.validityEndsAtString}</strong>
          </AdditionalZoneValidity>
          <TotalPrice>
            <Trans i18nKey="Generic.charged" /> {totalPrice}
          </TotalPrice>
        </>
      )}
      <RemainingFundsTitle>
        <Trans i18nKey="Generic.valueRemaining" />
      </RemainingFundsTitle>
      <RemainingFunds>{formatPrice(remainingFunds)}</RemainingFunds>

      <SeasonalTicketTitle>
        <Trans i18nKey="Generic.seasonalTicket" />
      </SeasonalTicketTitle>
      <SeasonalTicketZone>{seasonalTicket.zones}</SeasonalTicketZone>
      <SeasonalTicketValidity>
        <Trans i18nKey="Generic.validityAbbr" />{' '}
        <strong>{seasonalTicket.validityEndsAtString}</strong>
      </SeasonalTicketValidity>
    </AdditionalZoneConfirmation>
  );
};

AdditionalZoneConfirmationScreen.propTypes = {
  additionalZoneTicket: PropTypes.shape({
    additionalZones: PropTypes.string.isRequired,
    zone: PropTypes.string.isRequired,
    price: PropTypes.string.isRequired,
    validityEndsAtString: PropTypes.string.isRequired,
  }).isRequired,
  seasonalTicket: PropTypes.shape({
    zones: PropTypes.string.isRequired,
    validityEndsAtString: PropTypes.string.isRequired,
  }).isRequired,
  valueTicket: PropTypes.shape({
    zones: PropTypes.string.isRequired,
    passengers: PropTypes.string.isRequired,
    price: PropTypes.string.isRequired,
    validityEndsAtString: PropTypes.string.isRequired,
  }),
  remainingFunds: PropTypes.string.isRequired,
};

AdditionalZoneConfirmationScreen.defaultProps = {
  valueTicket: null,
};

export default AdditionalZoneConfirmationScreen;
